import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useCreateParliamentLiveFilters from 'components/Dashboard/ParliamentLive/useCreateParliamentLiveFilters';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

function useEventListFunctions() {
  const dateParam = getUrlParam('startDateTime');
  const fieldToFilter = getUrlParam('fieldToFilter') ?? 'createdAtUtc';
  const { generalApiCall } = useGeneralApiCall();
  const { currentUtcDateTime, createUKDate } = useCreateParliamentLiveFilters();

  const callEvents = async ({
    source,
    eventId,
    query = '',
    requestProperties: requestPropertiesProps,
    setInitialDate,
    specificDate,
  }) => {
    try {
      const currentUKDate = createUKDate({ specificDate });
      const pathname = `/api/search/query`;
      const initialValue =
        specificDate && !dateParam
          ? dayjs(specificDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : currentUKDate.format('HH:mm:ss') === '00:00:00'
            ? currentUKDate.format('YYYY-MM-DDTHH:mm:ss')
            : currentUKDate.utc()?.startOf('day').format('YYYY-MM-DDTHH:mm:ss');

      const secondValue =
        specificDate && !dateParam
          ? dayjs(specificDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
          : currentUtcDateTime.format('YYYY-MM-DDTHH:mm:ss');

      if (setInitialDate) {
        setInitialDate(secondValue);
      }
      let requestProperties = {
        query,
        type: 'RealtimeUtterance',
        filters: [
          {
            field: fieldToFilter,
            value: initialValue,
            operator: 'DATE_GTE',
          },
          {
            field: fieldToFilter,
            value: secondValue,
            operator: 'date_lt',
          },
        ],
        pageNumber: 1,
        pageSize: 10000,
      };
      if (eventId) {
        requestProperties = {
          ...requestProperties,
          filters: [
            ...requestProperties.filters,
            {
              field: 'eventId',
              value: `${eventId}`,
              operator: 'STR_EQ',
            },
          ],
          sort: {
            field: 'dateTime',
            order: 'ASC',
          },
          doNotIncludeCollapsedHits: false,
          doNotSearchTitle: true,
        };
      } else {
        requestProperties = {
          ...requestProperties,
          collapseField: 'eventId',
          doNotSearchTitle: true,
          sort: {
            field: 'title',
            order: 'ASC',
          },
          collapseSort: {
            field: 'dateTime',
            order: 'DESC',
          },
          includeExplanations: true,
          includeHighlights: true,
        };
      }
      return await generalApiCall({
        pathname,
        method: 'post',
        requestProperties: requestPropertiesProps ?? requestProperties,
        notShowErrorMessage: true,
        requestSource: source,
      });
    } catch (error) {}
  };

  return { callEvents };
}

const useCallEventListItem = ({ condition, eventId, data, setData, date: dateProp }) => {
  const { callEvents } = useEventListFunctions();
  const defaultInterval = 30000; //ms
  const dateParam = dateProp;
  const minutesPerCallParam = getUrlParam('minutesPerCall');
  const minutesPerCall = minutesPerCallParam ?? defaultInterval / 60000;
  const interval = minutesPerCallParam ? 60000 : defaultInterval;
  const fieldToFilter = getUrlParam('fieldToFilter') ?? 'createdAtUtc';

  const [date, setDate] = useState(dateParam);

  useEffect(() => {
    if (dateProp !== date) {
      setDate(dateProp);
    }
  }, [dateProp]);

  const intervallCallFunction = useRef(null);
  intervallCallFunction.current = async ({ source }) => {
    const secondDateValue = dayjs(date).add(minutesPerCall, 'minute').format('YYYY-MM-DDTHH:mm:ss');
    try {
      let requestProperties = {
        query: '',
        type: 'RealtimeUtterance',
        filters: [
          {
            field: 'eventId',
            value: `${eventId}`,
            operator: 'STR_EQ',
          },
          {
            field: fieldToFilter,
            value: date,
            operator: 'DATE_GTE',
          },
          {
            field: fieldToFilter,
            value: secondDateValue,
            operator: 'date_lt',
          },
        ],
        pageNumber: 1,
        pageSize: 10000,
        includeExplanations: true,
        includeHighlights: true,
        sort: {
          field: 'dateTime',
          order: 'ASC',
        },
      };

      let newRequestProperties = { ...requestProperties };
      let newFilters = [...newRequestProperties.filters];

      newRequestProperties = { ...newRequestProperties, filters: newFilters };
      let result = await callEvents({
        source,
        requestProperties: newRequestProperties,
        eventId,
      });
      let eventBodies = result?.hits?.map((item) => ({ ...item, notShown: true, text: item.body })) ?? [];
      setData((prev) => [...(prev ?? []), ...eventBodies]);
      setDate(secondDateValue);
    } catch (error) {}
  };

  const intervalFunc = useRef(null);
  useEffect(() => {
    const source = new AbortController();
    if (condition) {
      intervalFunc.current = setInterval(() => intervallCallFunction.current({ source }), interval);
    }
    return () => {
      if (!!source) {
        source.abort('Event call canceled by the user.');
      }
      if (!!intervalFunc.current) {
        clearInterval(intervalFunc.current);
      }
    };
  }, [condition]);
};

export { useCallEventListItem };

export default useEventListFunctions;
